'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { OnChangeFn, RowSelectionState, Updater } from '@tanstack/react-table';

export type TableRowSelections = Record<string, boolean>;
export type TableOnRowSelectionChange = (state: TableRowSelections) => void;

const stableObjectRef = {};

function getInitialRowSelections(controlledSelections?: TableRowSelections) {
  if (controlledSelections) {
    return controlledSelections;
  }

  return stableObjectRef;
}

export function useRowSelections(
  controlledRowSelections?: TableRowSelections,
  onRowSelect?: TableOnRowSelectionChange,
): [RowSelectionState, OnChangeFn<RowSelectionState>] {
  const hasUserUpdatedRef = useRef(false);
  const [state, setState] = useState<RowSelectionState>(getInitialRowSelections(controlledRowSelections));
  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    (updater: Updater<RowSelectionState>) => {
      const result = typeof updater === 'function' ? updater(state) : updater;
      setState(result);

      hasUserUpdatedRef.current = true;

      if (onRowSelect) {
        onRowSelect(result);
      }

      return result;
    },
    [state, onRowSelect],
  );

  useEffect(() => {
    if (!hasUserUpdatedRef.current) {
      setState(getInitialRowSelections(controlledRowSelections));
    }
  }, [controlledRowSelections]);

  return useMemo(() => [state, handleRowSelectionChange], [state, handleRowSelectionChange]);
}
